import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { appTitle } from "../common/GeneralConfigurations";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import CertificationTypesListForm from "./CertificationTypesListForm";
import { Space, Form, Popconfirm } from "antd";
import {
  deleteCertification,
  addCertification,
  updateCertification,
  getCertifications,
} from "../../api/CertificationApi";
import AdnMessageSuccess from "../common/adnMessage/AdnMessageSuccess";
import AdnMessageError from "../common/adnMessage/AdnMessageError";

const ManageCertificationTypesListPage = () => {
  const { t } = useTranslation();

  //States and consts
  const [certificationTypes, setCertificationTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalState, setModalState] = useState("");
  const [editCertification, setEditCertification] = useState();

  const [refetchData, setRefetchData] = useState(0);

  const [form] = Form.useForm();

  const title = t("certificationTypes.title");

  //Columns
  const columns = [
    {
      title: t("certificationTypes.columns.certificationDescription"),
      dataIndex: "description",
      width: "50%",
    },
    {
      title: t("certificationTypes.columns.certificationProvider"),
      dataIndex: "provider",
      width: "25%",
    },
    {
      title: t("certificationTypes.columns.actions"),
      key: "actions",
      render: (record) => (
        <Space style={{ display: "flex", justifyContent: "center" }}>
          <EditOutlined onClick={() => handleEdit(record)} />
          <Popconfirm
            title={t("certificationTypes.confirmations.deleteConfirmation")}
            onConfirm={() => handleDelete(record.id)}
          >
            <DeleteOutlined style={{ color: "red" }} />
          </Popconfirm>
        </Space>
      ),
      width: "15%",
    },
  ];

  //UseEffects
  useEffect(() => {
    document.title = `${appTitle} - ${title}`;
  }, [title]);

  useEffect(() => {
    loadCertificationTypes();
  }, [refetchData]);

  //Functions
  const loadCertificationTypes = () => {
    getCertifications()
      .then((res) => {
        if (res.data.result !== null) {
          setCertificationTypes(res.data);
          setTotalRecords(res.data.length);
        } else {
          setCertificationTypes([]);
        }
        setLoading(false);
      })
      .catch((err) => console.log(err));
  };

  const handleAdd = () => {
    setModalState(t("certificationTypes.modals.addCertificationType"));
    form.setFieldsValue({
      certificationDescription: "",
      certificationProvider: "",
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalState(t("certificationTypes.modals.editCertificationType"));
    form.setFieldsValue({
      certificationDescription: record.description,
      certificationProvider: record.provider,
    });
    setEditCertification(record.id);
    setModalVisible(true);
  };

  const handleDelete = (record) => {
    deleteCertification(record.id).then((res) => {
      if (res.status === 200) {
        AdnMessageSuccess();
        setRefetchData(refetchData + 1);
      } else {
        AdnMessageError(
          t("certificationTypes.messages.cantDeleteCertification")
        );
      }
    });
  };

  const handleFinish = (values) => {
    if (modalState === t("certificationTypes.modals.addCertificationType")) {
      let newCertification = {
        description: values.certificationDescription,
        provider: values.certificationProvider,
      };
      addCertification(newCertification).then((res) => {
        if (res.status === 201) {
          AdnMessageSuccess();
          setRefetchData(refetchData + 1);
        } else {
          AdnMessageError(
            t("certificationTypes.messages.cantAddCertification")
          );
        }
        setModalVisible(false);
      });
    } else if (
      modalState === t("certificationTypes.modals.editCertificationType")
    ) {
      let updatedCertification = {
        id: editCertification,
        description: values.certificationDescription,
        provider: values.certificationProvider,
      };
      updateCertification(updatedCertification).then((res) => {
        if (res.status === 200) {
          AdnMessageSuccess();
          setRefetchData(refetchData + 1);
        } else {
          AdnMessageError(
            t("certificationTypes.messages.cantUpdateCertification")
          );
        }
        setModalVisible(false);
      });
    }
  };

  return (
    <CertificationTypesListForm
      title={title}
      loading={loading}
      columns={columns}
      certificationTypes={certificationTypes}
      totalRecords={totalRecords}
      modalVisible={modalVisible}
      setModalVisible={setModalVisible}
      handleAdd={handleAdd}
      modalState={modalState}
      form={form}
      handleFinish={handleFinish}
    />
  );
};

export default ManageCertificationTypesListPage;
